import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User } from 'lucide-react';
import { getLinkIcon, getSocialIcon, socialPlatforms } from '../components/IconLibrary';
import {
  StarfieldBackground,
  MatrixBackground,
  ParticleBackground,
  AuroraBackground,
  FirefliesBackground,
  BubblesBackground,
  GeometricBackground,
  RainBackground,
  SnowBackground,
  WavesBackground
} from '../components/AnimatedBackgrounds';
import api from '../lib/api';
import { FONT_OPTION_BY_ID, googleFontsUrl } from '../lib/fonts';
import Windows98SkyBackground from '../components/Windows98SkyBackground';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get proper image URL
const getImageUrl = (url) => {
  if (!url) return null;
  // If URL already starts with /api/, use it directly (nginx will proxy)
  if (url.startsWith('/api/')) {
    return BACKEND_URL ? `${BACKEND_URL}${url}` : url;
  }
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return BACKEND_URL ? `${BACKEND_URL}${url}` : url;
};

// Theme background styles for static themes
const STATIC_THEME_STYLES = {
  solid: '#0f172a',
  gradient_cyber: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
  gradient_sunset: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
  gradient_ocean: 'linear-gradient(180deg, #0c1929 0%, #1a365d 50%, #0c1929 100%)',
  gradient_forest: 'linear-gradient(180deg, #0a1f0a 0%, #1a2f1a 50%, #0a1f0a 100%)',
  minimal_dark: '#0a0a0a',
  // Rendered specially below (bevel window/titlebar/buttons, not just a
  // background swap) — this entry only feeds the theme-picker preview
  // swatch and the loading/not-found fallback background.
  windows98: '#6b8cae'
};

// Animated themes list
const ANIMATED_THEMES = ['starfield', 'matrix', 'particles', 'aurora', 'fireflies', 'bubbles', 'geometric', 'rain', 'snow', 'waves'];

// Animated background component selector
const AnimatedBackground = ({ theme, color, color2 }) => {
  switch (theme) {
    case 'starfield':
      return <StarfieldBackground color={color} />;
    case 'matrix':
      return <MatrixBackground color={color} />;
    case 'particles':
      return <ParticleBackground color={color} color2={color2} />;
    case 'aurora':
      return <AuroraBackground color={color} color2={color2} />;
    case 'fireflies':
      return <FirefliesBackground color={color} />;
    case 'bubbles':
      return <BubblesBackground color={color} />;
    case 'geometric':
      return <GeometricBackground color={color} color2={color2} />;
    case 'rain':
      return <RainBackground color={color} />;
    case 'snow':
      return <SnowBackground color={color} />;
    case 'waves':
      return <WavesBackground color={color} />;
    default:
      return <StarfieldBackground color={color} />;
  }
};

const PublicCreatorPage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileImageError, setProfileImageError] = useState(false);

  // Reset image error when profile changes
  useEffect(() => {
    setProfileImageError(false);
  }, [profile?.profile_picture]);

  useEffect(() => {
    const fontId = profile?.theme_settings?.font;
    const url = googleFontsUrl(fontId);
    if (!url) return undefined;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.setAttribute('data-kc-creator-font', fontId);
    document.head.appendChild(link);
    return () => {
      if (link.parentNode === document.head) document.head.removeChild(link);
    };
  }, [profile?.theme_settings?.font]);

  // "Windows 98" is a full alternate layout (bevel window/titlebar/buttons,
  // not just a background swap), built on the same win98-scope CSS system
  // used by the app's own dashboard — see F:\Documents\ClaudeCode\win98-design-system.md.
  // Reusing that system here (rather than duplicating the bevel CSS as inline
  // styles) is what makes the buttons/inputs/dialogs below "just work."
  useEffect(() => {
    const isWin98 = profile?.theme_settings?.theme === 'windows98';
    document.body.classList.toggle('win98-scope', isWin98);
    return () => document.body.classList.remove('win98-scope');
  }, [profile?.theme_settings?.theme]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get(`/public/creator/${username}`);
        setProfile(response.data);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        toast.error('Creator not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [username, navigate]);

  const ensureProtocol = (url) => {
    if (!url) return '#';
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
  };

  // Fire-and-forget click tracking. Must NOT block navigation (iOS Safari
  // drops the user gesture after any await, which silently kills window.open).
  const handleLinkClick = (link) => {
    api.post(`/public/creator/links/${link.id}/click`).catch(() => {});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <p className="text-slate-400">Creator not found</p>
      </div>
    );
  }

  const themeSettings = profile.theme_settings || {};
  const themeName = themeSettings.theme || 'starfield';
  const isAnimated = ANIMATED_THEMES.includes(themeName);
  const staticBackground = STATIC_THEME_STYLES[themeName] || '#0f172a';
  const isWindows98 = themeName === 'windows98';

  // Windows 98 uses the real system palette (no glow, no gradients) — see
  // win98-design-system.md. Everything below reads `accent`/`accentAlt`
  // instead of hardcoding link_color/box_color directly, so the existing
  // avatar/hero-image markup doesn't need to be duplicated per theme.
  const accent = isWindows98 ? '#000080' : (themeSettings.link_color || '#06b6d4');
  const accentAlt = isWindows98 ? '#1084d0' : (themeSettings.box_color || '#d946ef');
  const glow = (color, alpha, px = 30) => (isWindows98 ? 'none' : `0 0 ${px}px ${color}${alpha}`);

  const headerContent = (
    <div className={isWindows98 ? 'text-center' : 'text-center mb-10'}>
      {(() => {
        const heroLayout = profile.hero_layout || 'none';
        const heroes = profile.hero_images || [];
        const HeroImg = ({ hero, sizeCls }) => {
          const img = (
            <img
              src={getImageUrl(hero.url)}
              alt="Featured cosplay"
              className={`${sizeCls} object-cover ${isWindows98 ? '' : 'rounded-2xl shadow-lg border-2'}`}
              style={isWindows98
                ? { border: '2px solid var(--win-dark-shadow)' }
                : { borderColor: accent, boxShadow: glow(accent, '33', 20) }}
              data-testid="hero-image"
            />
          );
          if (hero.share_token) {
            return (
              <a href={`/share/${hero.share_token}`} title="View cosplay" className={isWindows98 ? 'inline-block' : 'inline-block hover:scale-105 transition-transform'}>
                {img}
              </a>
            );
          }
          return img;
        };
        const avatar = profile.profile_picture && !profileImageError ? (
          <img
            src={getImageUrl(profile.profile_picture)}
            alt={profile.display_name || profile.username}
            className={`w-28 h-28 rounded-full object-cover ${isWindows98 ? '' : 'border-4 shadow-lg'}`}
            style={isWindows98
              ? { border: '3px solid var(--win-dark-shadow)' }
              : { borderColor: accent, boxShadow: glow(accent, '40') }}
            data-testid="profile-avatar"
            onError={() => {
              console.error('Failed to load profile image:', profile.profile_picture);
              setProfileImageError(true);
            }}
          />
        ) : (
          <div
            className="w-28 h-28 rounded-full flex items-center justify-center"
            style={isWindows98
              ? { background: 'var(--win-titlebar-start)', border: '3px solid var(--win-dark-shadow)' }
              : { background: `linear-gradient(135deg, ${accent}, ${accentAlt})`, boxShadow: glow(accent, '40') }}
            data-testid="profile-avatar-placeholder"
          >
            <User className="w-14 h-14 text-white" />
          </div>
        );
        if (heroLayout === 'above' && heroes[0]) {
          return (
            <>
              <div className="mb-4 flex justify-center">
                <HeroImg hero={heroes[0]} sizeCls="w-40 h-40" />
              </div>
              <div className="mb-4 flex justify-center">{avatar}</div>
            </>
          );
        }
        if (heroLayout === 'sides' && heroes[0] && heroes[1]) {
          return (
            <div className="mb-4 flex items-center justify-center gap-3">
              <HeroImg hero={heroes[0]} sizeCls="w-24 h-24" />
              {avatar}
              <HeroImg hero={heroes[1]} sizeCls="w-24 h-24" />
            </div>
          );
        }
        return <div className="mb-4 flex justify-center">{avatar}</div>;
      })()}

      {/* Display Name — Windows 98 puts this in the window titlebar instead,
          so it's skipped here for that theme (see the wrapper below). */}
      {!isWindows98 && (
        <h1
          className="text-3xl font-bold mb-1"
          style={{
            color: themeSettings.name_color || '#06b6d4',
            fontFamily: FONT_OPTION_BY_ID[themeSettings.font]?.family || undefined,
          }}
          data-testid="profile-display-name"
        >
          {profile.display_name || `@${profile.username}`}
        </h1>
      )}

      {/* Handle */}
      {profile.display_name && (
        <p className={isWindows98 ? 'text-lg mb-3 mt-3' : 'text-slate-400 text-lg mb-3'} style={isWindows98 ? { color: 'var(--win-text-disabled)' } : undefined} data-testid="profile-handle">
          @{profile.username}
        </p>
      )}

      {/* Bio */}
      {profile.bio && (
        <p className={isWindows98 ? 'max-w-md mx-auto leading-relaxed' : 'text-slate-300 max-w-md mx-auto leading-relaxed'} data-testid="profile-bio">
          {profile.bio}
        </p>
      )}

      {/* Social Links */}
      {profile.social_links && Object.keys(profile.social_links).length > 0 && (
        <div className={isWindows98 ? 'flex flex-wrap justify-center items-center gap-2 mt-6 w-full px-3' : 'flex justify-center items-center gap-2 mt-6 w-full'} data-testid="social-links">
          {Object.entries(profile.social_links).map(([platform, url]) => (
            <a
              key={platform}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={isWindows98
                ? 'win-btn win-btn-icon w-12 h-12 flex items-center justify-center'
                : 'flex-1 aspect-square min-w-0 max-w-[48px] rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-white hover:border-cyan-500 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all hover:-translate-y-1 hover:shadow-lg'}
              style={!isWindows98 ? { '--hover-shadow': `0 8px 20px ${themeSettings.link_color || '#06b6d4'}30` } : undefined}
              title={socialPlatforms[platform]?.label}
              data-testid={`social-${platform}`}
            >
              {getSocialIcon(platform, 'w-1/2 h-1/2')}
            </a>
          ))}
        </div>
      )}
    </div>
  );

  const linksContent = (
    <div className={isWindows98 ? 'space-y-2 mt-6' : 'space-y-4'}>
      {profile.links && profile.links.map(link => (
        <a
          key={link.id}
          href={ensureProtocol(link.url)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleLinkClick(link)}
          className={isWindows98
            ? 'win-btn w-full font-medium flex items-center justify-center gap-3'
            : 'w-full p-4 rounded-2xl font-semibold text-white transition-all hover:-translate-y-1 hover:shadow-xl active:-translate-y-1 active:shadow-xl flex items-center justify-center gap-3 active:scale-95'}
          style={isWindows98
            ? { WebkitTapHighlightColor: 'transparent', padding: '12px 16px' }
            : {
                background: `linear-gradient(135deg, ${themeSettings.link_color || '#06b6d4'}, ${themeSettings.box_color || '#d946ef'})`,
                boxShadow: `0 4px 15px ${themeSettings.link_color || '#06b6d4'}33`,
                WebkitTapHighlightColor: 'transparent'
              }}
          data-testid="public-link-button"
        >
          {link.icon && getLinkIcon(link.icon, 'w-5 h-5')}
          <span>{link.title}</span>
        </a>
      ))}

      {(!profile.links || profile.links.length === 0) && (
        <p className={isWindows98 ? 'text-center' : 'text-center text-slate-500'} style={isWindows98 ? { color: 'var(--win-text-disabled)' } : undefined}>No links available</p>
      )}
    </div>
  );

  const footerContent = (
    <div className={isWindows98 ? 'text-center mt-8 pt-4' : 'text-center mt-12 pt-6 border-t border-white/10'} style={isWindows98 ? { borderTop: '1px solid var(--win-shadow)' } : undefined}>
      <a
        href="/"
        className={isWindows98 ? 'text-sm' : 'text-slate-500 hover:text-cyan-400 transition-colors text-sm'}
        style={isWindows98 ? { color: 'var(--win-text-disabled)' } : undefined}
      >
        Powered by KyberCosplay
      </a>
    </div>
  );

  return (
    <div className="min-h-screen relative">
      {/* Background */}
      {isWindows98 ? (
        <Windows98SkyBackground />
      ) : isAnimated ? (
        <AnimatedBackground
          theme={themeName}
          color={themeSettings.link_color || '#06b6d4'}
          color2={themeSettings.box_color || '#d946ef'}
        />
      ) : (
        <div
          className="fixed inset-0 z-0"
          style={{ background: staticBackground }}
        />
      )}

      <div
        className={isWindows98 ? 'relative z-10 max-w-lg mx-auto px-4 py-12' : 'relative z-10 max-w-lg mx-auto px-4 py-12'}
        style={isWindows98 ? { fontFamily: 'var(--font-win, Tahoma, Verdana, sans-serif)' } : undefined}
        data-testid="public-creator-profile"
      >
        {isWindows98 ? (
          <div className="win-window">
            <div className="win-titlebar">
              <span className="text-xs tracking-wide truncate">{profile.display_name || `@${profile.username}`} — Public Profile</span>
            </div>
            <div className="p-4" style={{ background: 'var(--win-face)' }}>
              {headerContent}
              {linksContent}
              {footerContent}
            </div>
          </div>
        ) : (
          <>
            {headerContent}
            {linksContent}
            {footerContent}
          </>
        )}
      </div>
    </div>
  );
};

export default PublicCreatorPage;
